/**
 * Small controls the shell and every screen share: initials, truncation, record links, and a
 * focusable explanation. One accent for every avatar. Tooltips are short and clinical.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useId, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Panel } from "./layout.tsx";
import { Link } from "./ui.tsx";
export function Info({
  label,
  text
}: {
  readonly label: string;
  readonly text: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return <span onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} className="x1n2onr6 x3nfvp2 xxymvpz">
      <button type="button" aria-label={label} aria-describedby={open ? id : undefined} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} className="x3nfvp2 x6s0dn4 xl56j7k x15mio10 x2y5be4 x1717udv xc342km x1m3fsnl xjbqb8w x171263r x1jty25b x1cikjda xl4dl4e xqu410z x1e5flnj x1ypdohk x784prv x9v5kkp x1jubnyg x1bqaal">
        i
      </button>
      {open ? <span id={id} role="tooltip" className="x10l6tqk x1u8a7rm x1o0tod xdsb8wn x152e2qk x9f619 xezivpi x1vxnb6q x3bxlfn xcqm4px xzb3lse xmkeg23 x1y0btm7 xtkjqb x1fbwzx x8xb0n0 xl4dl4e xox936i xbz0q3o xeaf4i8 x1yc453h">
          {text}
        </span> : null}
    </span>;
}
export function LabelWithInfo({
  label,
  info,
  infoLabel
}: {
  readonly label: string;
  readonly info: string;
  readonly infoLabel: string;
}) {
  return <span className="x3nfvp2 x6s0dn4 xskm3c9">
      {label}
      <Info label={infoLabel} text={info} />
    </span>;
}

/** Two letters from a name, or from the email when there is no name. One colour everywhere. */
export function initials(name: string | null, email: string | null): string {
  const named = (name ?? "").trim();
  if (named !== "") {
    const parts = named.split(/\s+/).filter(part => part !== "");
    const first = parts[0]?.charAt(0) ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? "" : "";
    const letters = `${first}${last}`.toUpperCase();
    if (letters !== "") return letters;
  }
  const local = (email ?? "").split("@")[0] ?? "";
  const fromEmail = local.replaceAll(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase();
  return fromEmail === "" ? "?" : fromEmail;
}
export function Avatar({
  name,
  email,
  large = false
}: {
  readonly name: string | null;
  readonly email: string | null;
  readonly large?: boolean;
}) {
  return <span aria-hidden="true" {...{
    0: {
      className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1urbi3r x18fbomr x1m3fsnl xmbr0q7 x1fb0272 xl4dl4e xqu410z x1e5flnj"
    },
    1: {
      className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1m3fsnl xmbr0q7 x1fb0272 xl4dl4e xqu410z x1e5flnj x15mio10 x2y5be4"
    }
  }[!!large << 0]}>
      {initials(name, email)}
    </span>;
}
export function Truncate({
  value
}: {
  readonly value: string;
}) {
  return <span title={value} className="x1lliihq x1j1zfo6 xb3r6kr xlyipyv xuxw1ft">
      {value}
    </span>;
}

/** Truncated text whose full value appears on hover and keyboard focus. */
export function Reveal({
  value
}: {
  readonly value: string;
}) {
  const [open, setOpen] = useState(false);
  return <span tabIndex={0} title={value} aria-label={value} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} className="x1n2onr6 x1lliihq xeuugli x193iq5w">
      <Truncate value={value} />
      {open ? <span role="tooltip" className="x10l6tqk x1u8a7rm x1o0tod x10w3d4m xz4076n x1vxnb6q x3bxlfn xcqm4px xmkeg23 x1y0btm7 xtkjqb x1fbwzx x8xb0n0 xj0a0fe xl4dl4e">
          {value}
        </span> : null}
    </span>;
}
export function CopyValue({
  value,
  label
}: {
  readonly value: string;
  readonly label: string;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  async function copy() {
    const clip = navigator.clipboard;
    if (clip === undefined) {
      setFailed(true);
      setCopied(false);
      return;
    }
    try {
      await clip.writeText(value);
      setCopied(true);
      setFailed(false);
    } catch {
      setCopied(false);
      setFailed(true);
    }
  }
  return <span className="x3nfvp2 x6s0dn4 xskm3c9 xeuugli x193iq5w">
      <Truncate value={value} />
      <button type="button" aria-label={copied ? `${label} copied` : `Copy ${label}`} onClick={() => void copy()} className="x2lah0s xxi9w3g xc7sml1 xmkeg23 x1y0btm7 xtkjqb x1fbwzx xcqm4px x171263r xzb3lse x1cikjda xl4dl4e xqu410z x1ypdohk x784prv x9v5kkp x1jubnyg x7s97pk">
        {copied ? "Copied" : "Copy"}
      </button>
      {failed ? <span className="x156x1qg xl4dl4e">Could not copy</span> : null}
    </span>;
}
export function Person({
  name,
  email,
  to
}: {
  readonly name: string;
  readonly email?: string | null;
  readonly to?: string;
}) {
  const face = <span className="x3nfvp2 x6s0dn4 x1173whf xeuugli x193iq5w xxymvpz">
      <Avatar name={name} email={email ?? null} />
      <span className="x78zum5 xdt5ytf xeuugli">
        <Truncate value={name} />
        {email === undefined || email === null || email === "" ? null : <span className="x1jty25b">
            <Truncate value={email} />
          </span>}
      </span>
    </span>;
  if (to === undefined) return face;
  return <Link to={to}>{face}</Link>;
}
export function RecordLink({
  to,
  relation,
  label
}: {
  readonly to: string;
  readonly relation: string;
  readonly label: string;
}) {
  return <Link to={to}>
      <span className="x1jty25b xox936i">{relation}</span> {label}
    </Link>;
}
export interface RelatedLink {
  readonly relation: string;
  readonly label: string;
  readonly to: string;
}

/** Who this record is, what it connects to, and what the role can do here. */
export function Context({
  title,
  who,
  role,
  can,
  links
}: {
  readonly title: string;
  readonly who: ReactNode;
  readonly role?: ReactNode;
  readonly can: string;
  readonly links: readonly RelatedLink[];
}) {
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">{title}</h2>
        {who}
        {role === undefined ? null : <p className="x1ghz6dp x1jty25b xl4dl4e">{role}</p>}
        <p className="x1ghz6dp x1jty25b xl4dl4e">{can}</p>
        {links.length === 0 ? null : <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf x1173whf">
            {links.map(link => <li key={`${link.relation}-${link.to}`}>
                <RecordLink to={link.to} relation={link.relation} label={link.label} />
              </li>)}
          </ul>}
      </div>
    </Panel>;
}
export function SaveLine({
  phase
}: {
  readonly phase: "idle" | "saving" | "saved" | "failed";
}) {
  if (phase === "idle") return null;
  const text = phase === "saving" ? "Saving" : phase === "saved" ? "Saved" : "Not saved";
  return <p role="status" {...{
    0: {
      className: "x1ghz6dp xl4dl4e xqu410z xt3l3uh x149xavy xa02eh3"
    },
    2: {
      className: "x1ghz6dp xl4dl4e xqu410z xt3l3uh x149xavy xa02eh3 x1p2vmyc"
    },
    1: {
      className: "x1ghz6dp xl4dl4e xqu410z xt3l3uh x149xavy xa02eh3 x156x1qg"
    },
    3: {
      className: "x1ghz6dp xl4dl4e xqu410z xt3l3uh x149xavy xa02eh3 x156x1qg"
    }
  }[!!(phase === "saved") << 1 | !!(phase === "failed") << 0]}>
      {text}
    </p>;
}